import { CwLog } from 'cw-ui-core';
import React from 'react';
import AsioNeonResponsiveThemeProvider from 'src/theme/AsioNeonResponsiveThemeProvider';

import AddExternalIdPContainer from '../features/authentication-v2/components/AddExternalIdP/AddExternalIdPContainer';
import EditExternalIdPContainer from '../features/authentication-v2/components/EditExternalIdP/EditExternalIdPContainer';
import { SkeletonLoader } from './SkeletonLoader/SkeletonLoader';
import UsersContainer from '../features/users';

// Lazy load the screen components
const ImportedUsersContainer = React.lazy(
    () => import('../features/users/components/ImportedUsers/ImportedUsers/ImportedUsersContainer')
);
const ProductsContainer = React.lazy(() => import('../features/products/components/Products/ProductsContainer'));
const RolesContainer = React.lazy(() => import('../features/identity-roles/components/RolesContainer'));
const SupportContainer = React.lazy(() => import('../pages/Support/SupportContainer'));
const ConfigureApplicationContainer = React.lazy(
    () => import('../features/applications/components/ConfigureApplicationContainer')
);
const ConfirmApplicationContainer = React.lazy(
    () => import('../features/applications/components/ConfirmApplicationContainer')
);
const ApplicationRoles = React.lazy(() => import('../features/application-roles/index'));
const NewIdentityRolesContainer = React.lazy(
    () => import('../features/identity-roles-v2/components/NewIdentityRolesContainer')
);
const NewAuthenticationContainer = React.lazy(
    () => import('../features/authentication-v2/components/NewAuthentication/NewAuthenticationContainer')
);
const NewUsersContainer = React.lazy(() => import('../features/users-v2/components/NewUsers/NewUsersContainer'));
const GuestUsersContainer = React.lazy(() => import('../features/guest-users'));
const AddUserContainer = React.lazy(() => import('../features/users-v2/components/AddUser/AddUserContainer'));

const AddDuoMFAContainer = React.lazy(
    () => import('../features/authentication-v2/components/AddDuoMFA/AddDuoMFAContainer')
);
const EditDuoMFAContainer = React.lazy(
    () => import('../features/authentication-v2/components/EditDuoMFA/EditDuoMFAContainer')
);

const ProductsV2 = React.lazy(() => import('../features/products-v2/Products'));
const Subscribers = React.lazy(() => import('../features/subscribers/Subscribers'));

const getScreen = (screenId, props = null) => {
    switch (screenId) {
        case 'Users':
        case 'PendingInvites':
            return <UsersContainer key={screenId} {...props} />;

        case 'ImportedUsers':
            return <ImportedUsersContainer key={screenId} {...props} />;

        case 'Products':
            return <ProductsContainer key={screenId} {...props} />;

        case 'ProductsV2':
            return <ProductsV2 key={screenId} {...props} />;

        case 'Subscribers':
            return <Subscribers key={screenId} {...props} />;

        case 'Roles':
            return <RolesContainer key={screenId} {...props} />;

        case 'Support':
            return <SupportContainer key={screenId} {...props} />;

        case 'ConfigureApplication':
            return <ConfigureApplicationContainer key={screenId} {...props} />;

        case 'ConfirmApplication':
            return <ConfirmApplicationContainer key={screenId} {...props} />;

        case 'ApplicationRoles':
            return <ApplicationRoles key={screenId} {...props} />;

        case 'NewIdentityRoles':
            return <NewIdentityRolesContainer key={screenId} {...props} />;

        case 'NewAuthentication':
            return <NewAuthenticationContainer key={screenId} {...props} />;

        case 'NewUsers':
            return <NewUsersContainer key={screenId} {...props} />;

        case 'GuestUsers':
            return <GuestUsersContainer key={screenId} {...props} />;

        case 'AddUser':
            return <AddUserContainer key={screenId} {...props} />;

        case 'AddExternalIdP':
            return <AddExternalIdPContainer key={screenId} {...props} />;

        case 'EditExternalIdP':
            return <EditExternalIdPContainer key={screenId} {...props} />;

        case 'AddDuoMFA':
            return <AddDuoMFAContainer key={screenId} {...props} />;

        case 'EditDuoMFA':
            return <EditDuoMFAContainer key={screenId} {...props} />;

        default:
            CwLog.error(`getScreen -> No screen found for ${screenId}`);
            return null;
    }
};

const getScreenFactory = (screenId, props, fallback = <SkeletonLoader />) => {
    if (!screenId) {
        return null;
    }
    return (
        <AsioNeonResponsiveThemeProvider>
            <React.Suspense fallback={fallback}>{getScreen(screenId, props)}</React.Suspense>
        </AsioNeonResponsiveThemeProvider>
    );
};

export default getScreenFactory;
